import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useDropzone } from 'react-dropzone';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import {
  Globe,
  Upload,
  CheckCircle,
  Loader2,
  FileText,
  AlertCircle,
  Camera,
  FolderOpen,
  ArrowRight,
  ArrowLeft,
  Send,
  Volume2,
  Sparkles,
  Clock,
  Shield,
  X,
  ChevronDown
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { getTier1Languages, getStoredLanguage, setStoredLanguage, getLanguageByCode } from '../lib/languages';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Get Tier 1 languages for invite page (full UI support)
const languages = getTier1Languages().map(lang => ({
  code: lang.code,
  name: lang.name,
  native: lang.nativeName,
  displayLabel: lang.displayLabel
}));

export default function InvitePage() {
  const { token } = useParams();
  const location = useLocation();
  const { t, language: currentLanguage, setLanguage: setAppLanguage } = useTranslation();
  const [step, setStep] = useState(0); // 0: Language, 1: Intro, 2: Documents, 3: Complete
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [introData, setIntroData] = useState(null);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);

  // Account creation state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [existingUserRole, setExistingUserRole] = useState(null);
  const [selectedRole, setSelectedRole] = useState('individual');

  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const [errors, setErrors] = useState({});

  // Check for preselected language from LanguagePickerPage or localStorage
  useEffect(() => {
    // First check location state (from LanguagePickerPage)
    const stateLanguage = location.state?.language;
    if (stateLanguage) {
      setSelectedLanguage(stateLanguage);
      setStoredLanguage(stateLanguage);
      setAppLanguage(stateLanguage); // Sync with main i18n system
      setStep(1); // Skip language selection
      return;
    }

    // Then check localStorage
    const storedLanguage = getStoredLanguage();
    if (storedLanguage) {
      setSelectedLanguage(storedLanguage);
      setAppLanguage(storedLanguage); // Sync with main i18n system
      // Preselect but still show language picker so user can change
    }
  }, [location.state, setAppLanguage]);

  // Validate token and get basic info
  const { data: inviteInfo, isLoading, error } = useQuery({
    queryKey: ['invite', token],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/invite/${token}`);
      if (!response.ok) throw new Error('Invalid invite');
      const data = await response.json();
      if (data.existing_user_role) {
        setExistingUserRole(data.existing_user_role);
        setSelectedRole(data.existing_user_role);
      }
      return data;
    }
  });

  // Initialize invite with selected language
  const initializeMutation = useMutation({
    mutationFn: async (language) => {
      const response = await fetch(`${API_URL}/api/invites/${token}/initialize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selected_language: language })
      });
      if (!response.ok) throw new Error('Failed to initialize');
      return response.json();
    },
    onSuccess: (data) => {
      setIntroData(data);
      fetchClientIntro(data);
      // Initialize conversation for chat
      initializeConversation();
    }
  });

  // Initialize or fetch conversation
  const initializeConversation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversations/client-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invite_token: token })
      });
      if (response.ok) {
        const conv = await response.json();
        setConversationId(conv._id);
      }
    } catch (e) {
      console.error('Failed to initialize conversation', e);
    }
  };

  // Fetch messages for polling
  const fetchMessages = async () => {
    if (!conversationId) return;
    try {
      const response = await fetch(
        `${API_URL}/api/conversations/${conversationId}/messages-by-token?invite_token=${token}`
      );
      if (response.ok) {
        const msgs = await response.json();
        setChatMessages(msgs);
      }
    } catch (e) {
      console.error('Failed to fetch messages', e);
    }
  };

  // Poll for messages when chat is open
  useEffect(() => {
    if (chatOpen && conversationId) {
      fetchMessages();
      pollIntervalRef.current = setInterval(fetchMessages, 5000);
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [chatOpen, conversationId]);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Send message
  const sendMessage = async () => {
    if (!messageInput.trim() || !conversationId) return;

    setSendingMessage(true);
    try {
      const response = await fetch(`${API_URL}/api/conversations/${conversationId}/messages-by-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invite_token: token,
          text: messageInput.trim()
        })
      });

      if (response.ok) {
        setMessageInput('');
        fetchMessages();
        toast.success(t('invite.chat.sent'));
      }
    } catch (e) {
      toast.error(t('error.generic'));
    }
    setSendingMessage(false);
  };

  const fetchClientIntro = async (data) => {
    if (data?.office?._id) {
      try {
        const response = await api.post('/ai/client-intro', {
          office_id: data.office._id,
          client_language: selectedLanguage
        });
        setIntroData(prev => ({ ...prev, ...response }));
      } catch (e) {
        console.error('Failed to get intro', e);
      }
    }
    setStep(1);
  };

  const handleLanguageSelect = (langCode) => {
    setSelectedLanguage(langCode);
    setStoredLanguage(langCode);
    setAppLanguage(langCode); // Sync with main i18n system
    initializeMutation.mutate(langCode);
  };

  const onDrop = useCallback(async (acceptedFiles, docItem) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/invite/${token}/upload`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();

        setValidating(true);
        try {
          const validationResponse = await fetch(`${API_URL}/api/ai/validate-document`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              document_id: result._id,
              expected_type: docItem,
              client_language: selectedLanguage
            })
          });

          if (validationResponse.ok) {
            const validation = await validationResponse.json();
            setUploadedDocs(prev => ({
              ...prev,
              [docItem]: {
                ...result,
                validation,
                file_name: file.name
              }
            }));

            if (!validation.matches_expected) {
              toast.warning(validation.message || "This doesn't look like the expected document");
            } else {
              toast.success('Document uploaded and verified!');
            }
          }
        } catch (e) {
          setUploadedDocs(prev => ({
            ...prev,
            [docItem]: { ...result, file_name: file.name }
          }));
          toast.success('Document uploaded!');
        }
        setValidating(false);
      } else {
        toast.error('Upload failed');
      }
    } catch (err) {
      toast.error('Upload failed');
    }
    setUploading(false);
  }, [token, selectedLanguage]);

  const markComplete = async () => {
    try {
      await fetch(`${API_URL}/api/invite/${token}/update-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'complete' })
      });
      setStep(3);
    } catch (err) {
      toast.error('Failed to complete');
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setErrors({});
    const newErrors = {};

    // Validation
    if (!password) {
      newErrors.password = t('auth.login.password') + " is required";
    }

    if (!existingUserRole) {
      if (password && password.length < 8) {
        newErrors.password = t('auth.signup.passwordTooShort') || "Password must be at least 8 characters";
      } else if (password) {
        // Complexity check
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (!hasUpperCase || !hasLowerCase || !hasSpecialChar) {
          newErrors.password = "Password must contain at least 1 uppercase, 1 lowercase, and 1 special character";
        }
      }

      if (password && password.length > 100) {
        newErrors.password = t('auth.signup.passwordTooLong') || "Password cannot exceed 100 characters";
      }
      if (password !== confirmPassword) {
        newErrors.confirmPassword = t('auth.signup.passwordsDoNotMatch') || "Passwords do not match";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setCreatingAccount(true);
    try {
      const data = await api.post(`/invite/${token}/convert-to-user`, {
        password,
        preferred_language: selectedLanguage,
        role: selectedRole
      }, { skipAuth: true });

      // Success! Login and redirect
      localStorage.setItem('token', data.token);
      toast.success(t('auth.signup.success'));

      // Force reload to update auth state and redirect to dashboard
      window.location.href = '/analytics';

    } catch (err) {
      // Handle specific backend errors
      if (err.message === "Invalid password for existing account" || err.status === 400) {
        setErrors({ password: "Incorrect password, please try again." });
        return;
      }
      toast.error(err.message);
    } finally {
      setCreatingAccount(false);
    }
  };

  const documentChecklist = introData?.document_checklist || [];
  const completedDocs = Object.keys(uploadedDocs).length;
  const progress = documentChecklist.length > 0
    ? Math.round((completedDocs / documentChecklist.length) * 100)
    : 0;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30" data-testid="invite-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !inviteInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30" data-testid="invite-error">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">{t('invite.error.title')}</h2>
            <p className="text-muted-foreground">
              {t('invite.error.message')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="invite-page">
      {/* Header */}
      <header className="bg-background border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img
                src="/assets/pocketfiler-header-logo.png"
                alt="PocketFiler"
                className="h-20"
                style={{ objectFit: 'contain' }}
              />
            </div>
            {step > 0 && step < 3 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>{introData?.estimated_time || '5-10 min'}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-lg pb-24">
        {/* Step 0: Language Selection */}
        {step === 0 && (
          <Card className="animate-fade-in" data-testid="language-selection">
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-accent" />
              </div>
              <CardTitle className="text-2xl">{t('invite.language.title')}</CardTitle>
              <CardDescription>
                {t('invite.language.subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {languages.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageSelect(lang.code)}
                    disabled={initializeMutation.isPending}
                    className="p-4 rounded-lg border-2 hover:border-primary hover:bg-primary/5 transition-all text-left group min-h-[72px]"
                    data-testid={`language-${lang.code}`}
                  >
                    <p className="font-medium group-hover:text-primary">{lang.native}</p>
                    <p className="text-sm text-muted-foreground">{lang.name}</p>
                  </button>
                ))}
              </div>
              {initializeMutation.isPending && (
                <div className="flex items-center justify-center mt-4">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 1: AI Intro */}
        {step === 1 && (
          <Card className="animate-fade-in" data-testid="ai-intro">
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8 text-accent" />
              </div>
              <CardTitle className="text-2xl">
                {t('invite.welcome.title')} {introData?.organization_name || 'PocketFiler'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* AI Generated Intro */}
              <div className="p-4 rounded-lg bg-accent/5 border border-accent/20">
                <p className="text-sm">
                  {introData?.intro || (documentChecklist.length > 0 ? t('invite.welcome.subtitle') : "Welcome! Please create your account to get started.")}
                </p>
              </div>

              {/* Time Estimate - Only show if there are documents */}
              {documentChecklist.length > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{t('invite.welcome.timeLabel')}</p>
                    <p className="text-xs text-muted-foreground">{introData?.estimated_time || t('invite.welcome.time')}</p>
                  </div>
                </div>
              )}

              {/* Document List Preview */}
              {documentChecklist.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">{t('invite.documents.needed')}:</p>
                  <div className="space-y-2">
                    {documentChecklist.map((doc, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <FileText className="h-4 w-4" />
                        <span>{typeof doc === 'string' ? doc : doc.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Security Notice - Only show if there are documents */}
              {documentChecklist.length > 0 && (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-success/5 border border-success/20">
                  <Shield className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-success">{t('invite.welcome.secure')}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('invite.welcome.secureDesc')}
                    </p>
                  </div>
                </div>
              )}

              <Button
                onClick={() => {
                  if (documentChecklist?.length > 0) {
                    setStep(2);
                  } else {
                    markComplete();
                  }
                }}
                className="w-full min-h-[48px]"
                size="lg"
                data-testid="start-upload-button"
              >
                {documentChecklist?.length > 0
                  ? t('invite.welcome.start')
                  : (existingUserRole ? "Accept Invite & Log In" : "Create Account")
                }
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Document Upload */}
        {step === 2 && (
          <div className="space-y-4 animate-fade-in" data-testid="document-upload">
            {/* Progress */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium">{t('invite.upload.progress')}</span>
                  <span className="text-muted-foreground">{completedDocs} {t('common.of')} {documentChecklist.length}</span>
                </div>
                <Progress value={progress} className="h-2" />
              </CardContent>
            </Card>

            {/* Current Document */}
            {documentChecklist[currentDocIndex] && (
              <DocumentUploadCard
                docItem={typeof documentChecklist[currentDocIndex] === 'string'
                  ? documentChecklist[currentDocIndex]
                  : documentChecklist[currentDocIndex].name}
                docIndex={currentDocIndex}
                totalDocs={documentChecklist.length}
                uploadedDoc={uploadedDocs[typeof documentChecklist[currentDocIndex] === 'string'
                  ? documentChecklist[currentDocIndex]
                  : documentChecklist[currentDocIndex].name]}
                onDrop={(files) => onDrop(files, typeof documentChecklist[currentDocIndex] === 'string'
                  ? documentChecklist[currentDocIndex]
                  : documentChecklist[currentDocIndex].name)}
                uploading={uploading}
                validating={validating}
                onNext={() => {
                  if (currentDocIndex < documentChecklist.length - 1) {
                    setCurrentDocIndex(prev => prev + 1);
                  } else {
                    markComplete();
                  }
                }}
                onPrev={() => setCurrentDocIndex(prev => Math.max(0, prev - 1))}
                onSkip={() => {
                  if (currentDocIndex < documentChecklist.length - 1) {
                    setCurrentDocIndex(prev => prev + 1);
                  } else {
                    markComplete();
                  }
                }}
                isFirst={currentDocIndex === 0}
                isLast={currentDocIndex === documentChecklist.length - 1}
                language={selectedLanguage}
                t={t}
              />
            )}
          </div>
        )}

        {/* Step 3: Complete & Create Account */}
        {step === 3 && (
          <Card className="animate-fade-in" data-testid="upload-complete">
            <CardContent className="pt-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-10 w-10 text-success" />
                </div>
                <h2 className="text-2xl font-bold mb-2">{t('invite.complete.title')}</h2>
                <p className="text-muted-foreground">
                  {t('invite.complete.subtitle')}
                </p>
              </div>

              {/* Account Creation / Login Form */}
              <div className="p-6 rounded-xl border-2 border-primary/20 bg-primary/5 mb-8">
                <h3 className="text-lg font-semibold mb-2 text-center">
                  {existingUserRole ? "Link to Your Account" : "Create Your Account"}
                </h3>
                <p className="text-sm text-center text-muted-foreground mb-6">
                  {existingUserRole
                    ? `You already have an account as a ${existingUserRole}. Login to link this invite.`
                    : `Set a password to access your dashboard, track these documents, and chat with ${introData?.organization_name || 'us'}.`
                  }
                </p>

                <form onSubmit={handleCreateAccount} className="space-y-4">
                  {/* Role Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Account Role</label>
                    <div className="relative">
                      <select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        disabled={!!existingUserRole}
                        className="w-full p-2 rounded-md border bg-background disabled:opacity-70 disabled:bg-muted"
                      >
                        <option value="individual">Individual</option>
                        <option value="business">Business</option>
                        <option value="buyer">Buyer</option>
                        <option value="seller">Seller</option>
                      </select>
                    </div>
                    {!existingUserRole && (
                      <p className="text-xs text-muted-foreground">
                        Select 'Business' if you represent a company.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="password">
                      {existingUserRole ? "Enter Password" : "Password"} <span className="text-destructive ml-1">*</span>
                    </label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) setErrors(prev => ({ ...prev, password: null }));
                      }}
                      placeholder={existingUserRole ? "Your account password" : "Min. 8 characters"}
                      required
                      maxLength={100}
                      className={errors.password ? "border-destructive" : ""}
                    />
                    {errors.password && <p className="text-xs text-destructive mt-1">{errors.password}</p>}
                    {!errors.password && !existingUserRole && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Must contain 8+ chars, 1 uppercase, 1 lowercase, 1 special char.
                      </p>
                    )}
                  </div>

                  {!existingUserRole && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium" htmlFor="confirmPassword">
                        Confirm Password <span className="text-destructive ml-1">*</span>
                      </label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: null }));
                        }}
                        placeholder="Re-enter password"
                        required
                        maxLength={100}
                        className={errors.confirmPassword ? "border-destructive" : ""}
                      />
                      {errors.confirmPassword && <p className="text-xs text-destructive mt-1">{errors.confirmPassword}</p>}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={creatingAccount}
                  >
                    {creatingAccount ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {existingUserRole ? "Logging in..." : "Creating Account..."}
                      </>
                    ) : (
                      <>
                        {existingUserRole ? "Login & View Dashboard" : "Create Account & View Dashboard"}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Summary */}
              <div className="text-left p-4 rounded-lg bg-muted mb-6">
                <p className="text-sm font-medium mb-3">{t('invite.complete.summary')}:</p>
                <div className="space-y-2">
                  {Object.entries(uploadedDocs).map(([name, doc]) => (
                    <div key={name} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-success" />
                      <span>{doc.file_name || name}</span>
                    </div>
                  ))}
                  {Object.keys(uploadedDocs).length === 0 && (
                    <p className="mt-2 text-sm text-center">Join <strong>{inviteInfo.organization_name}</strong> on PocketFiler.</p>
                  )}
                </div>
              </div>

              {/* Security Notice */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted text-left">
                <Shield className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  {t('invite.complete.securityNote')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Floating Chat Widget - visible on all steps after language selection */}
      {step > 0 && (
        <div className="fixed bottom-20 right-4 z-50" data-testid="chat-widget">
          {chatOpen ? (
            <Card className="w-80 h-96 flex flex-col shadow-lg animate-fade-in">
              {/* Chat Header */}
              <CardHeader className="flex-shrink-0 py-3 px-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img src="/assets/chat.svg" alt="" className="h-5 w-5" aria-hidden="true" />
                    <CardTitle className="text-sm font-medium">{t('invite.chat.title')}</CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setChatOpen(false)}
                    data-testid="close-chat-button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>

              {/* Chat Messages */}
              <ScrollArea className="flex-1 p-3" data-testid="chat-messages">
                {chatMessages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center py-8">
                    <img src="/assets/discussion.svg" alt="" className="h-12 w-12 opacity-50 mb-3" />
                    <p className="text-xs text-muted-foreground">{t('invite.chat.empty')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {chatMessages.map((msg) => {
                      const isClient = msg.from?.type === 'client';
                      // Client sees translated text from business, original text from self
                      const displayText = isClient ? msg.original_text : (msg.translated_text || msg.original_text);

                      return (
                        <div
                          key={msg._id}
                          className={`flex ${isClient ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[80%] p-2 rounded-lg text-sm ${isClient
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                            }`}>
                            {displayText}
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Chat Input */}
              <div className="p-2 border-t flex gap-2">
                <Input
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder={t('invite.chat.placeholder')}
                  className="text-sm"
                  disabled={sendingMessage}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && messageInput.trim()) {
                      sendMessage();
                    }
                  }}
                  data-testid="chat-input"
                />
                <Button
                  size="icon"
                  disabled={!messageInput.trim() || sendingMessage}
                  onClick={sendMessage}
                  data-testid="send-chat-button"
                >
                  {sendingMessage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </Card>
          ) : (
            <Button
              size="lg"
              className="rounded-full w-14 h-14 shadow-lg"
              onClick={() => setChatOpen(true)}
              data-testid="open-chat-button"
            >
              <img src="/assets/chat.svg" alt="" className="h-6 w-6 invert" aria-hidden="true" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Document Upload Card Component
function DocumentUploadCard({
  docItem,
  docIndex,
  totalDocs,
  uploadedDoc,
  onDrop,
  uploading,
  validating,
  onNext,
  onPrev,
  onSkip,
  isFirst,
  isLast,
  language,
  t
}) {
  const fileInputRef = React.useRef(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'text/*': ['.txt']
    },
    maxFiles: 1,
    noClick: true, // We'll handle clicks manually for mobile
    noKeyboard: true
  });

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      onDrop(files);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            {docIndex + 1}. {docItem}
          </CardTitle>
          <span className="text-sm text-muted-foreground">
            {docIndex + 1} {t('common.of')} {totalDocs}
          </span>
        </div>
        <CardDescription>
          {t('invite.upload.dragDrop')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!uploadedDoc ? (
          <>
            {/* Hidden file inputs */}
            {/* Camera input: id-linked to label below so the browser treats it as a direct user gesture and honours capture="environment" */}
            <input
              id="camera-capture-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileChange}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf,.pdf,.png,.jpg,.jpeg,.txt"
              className="hidden"
              onChange={handleFileChange}
            />

            <div
              {...getRootProps()}
              data-testid="client-upload-input"
              className={`dropzone ${isDragActive ? 'active' : ''} min-h-[150px] flex flex-col items-center justify-center`}
            >
              <input {...getInputProps()} />
              {uploading || validating ? (
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {validating ? t('invite.upload.analyzing') : t('invite.upload.uploading')}
                  </p>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    {t('invite.upload.dragDrop')}
                  </p>
                  <div className="flex gap-4">
                    <label
                      htmlFor="camera-capture-input"
                      className="flex flex-col items-center gap-2 p-3 min-h-[72px] min-w-[80px] rounded-lg border hover:bg-muted transition-colors cursor-pointer"
                      aria-label={t('invite.upload.takePhoto')}
                    >
                      <Camera className="h-6 w-6 text-primary" />
                      <span className="text-xs text-muted-foreground">{t('invite.upload.takePhoto')}</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleBrowseClick}
                      className="flex flex-col items-center gap-2 p-3 min-h-[72px] min-w-[80px] rounded-lg border hover:bg-muted transition-colors"
                      aria-label={t('invite.upload.browse')}
                    >
                      <FolderOpen className="h-6 w-6 text-primary" />
                      <span className="text-xs text-muted-foreground">{t('invite.upload.browse')}</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-2">
              {!isFirst && (
                <Button variant="outline" onClick={onPrev} className="flex-1 min-h-[48px]">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  {t('common.back')}
                </Button>
              )}
              <Button variant="ghost" onClick={onSkip} className="flex-1 min-h-[48px]">
                {t('invite.upload.skip')}
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="p-4 rounded-lg border bg-success/5 border-success/20">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-success" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{uploadedDoc.file_name}</p>
                  <p className="text-xs text-muted-foreground">{t('invite.upload.success')}</p>
                </div>
              </div>

              {uploadedDoc.validation && (
                <div className={`mt-3 p-2 rounded text-sm ${uploadedDoc.validation.matches_expected
                  ? 'bg-success/10 text-success'
                  : 'bg-warning/10 text-warning'
                  }`}>
                  {uploadedDoc.validation.message}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {!isFirst && (
                <Button variant="outline" onClick={onPrev}>
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  {t('common.back')}
                </Button>
              )}
              <Button onClick={onNext} className="flex-1">
                {isLast ? t('invite.upload.finish') : t('common.next')}
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
